import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useTeams } from '@/hooks/useTeams';
import { useTeamCapacity } from '@/hooks/kpi/useTeamCapacity';
import { Users, Clock, Wrench, TrendingUp } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
// DTC-J: route the per-team capacity "today" filter through the
// canonical shop-tz helper so a viewer in a different browser TZ than
// the shop sees the correct shop-local "today" capacity. The pre-DTC-J
// `format(new Date(), 'yyyy-MM-dd')` could surface yesterday/tomorrow
// during evening hours for cross-TZ viewers.
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';

interface TeamCapacityData {
  teamId: string;
  teamName: string;
  isDefault: boolean;
  technicianCount: number;
  totalCapacityMinutes: number;
  usedMinutes: number;
  activeTickets: number;
  utilizationPercent: number;
}

export function TeamCapacityCard() {
  const { activeShop } = useAuth();
  const { isLoading: teamsLoading } = useTeams();
  const { shopTimezone } = useShopTimezone();
  const today = getTodayInShopTz(shopTimezone);

  // K-T-13-B: per-team capacity + today's planned workload sourced
  // from canonical `kpi_team_capacity` RPC. Replaces the prior inline
  // shift_start/shift_end parsing + 480 fallback + local utilization
  // math. Capacity now honors the full kpi_technician_capacity
  // resolution chain (shifts > daily_capacity_minutes > 480 fallback,
  // with efficiency / buffer multipliers) — pre-K-T-13-B the widget
  // ignored eff/buf/dcm and used raw shift duration.
  const { data: capacityRows, isLoading: capacityLoading } = useTeamCapacity({
    shopId: activeShop?.id ?? '',
    day: today,
  });

  const isLoading = teamsLoading || capacityLoading;

  const capacityData: TeamCapacityData[] = (capacityRows ?? [])
    .map((r) => ({
      teamId: r.team_id,
      teamName: r.team_name,
      isDefault: r.is_default,
      technicianCount: r.tech_count,
      totalCapacityMinutes: r.capacity_minutes,
      usedMinutes: r.booked_minutes,
      activeTickets: r.active_ticket_count,
      // The widget caps at 100% for visual fit (the canonical
      // primitive returns raw values; clamping is a UI concern).
      utilizationPercent: r.capacity_minutes > 0
        ? Math.min(100, Math.round((r.booked_minutes / r.capacity_minutes) * 100))
        : 0,
    }));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Team Capacity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!capacityData?.length) {
    return null;
  }

  const formatMinutes = (mins: number) => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getUtilizationColor = (percent: number) => {
    if (percent < 50) return 'bg-emerald-500';
    if (percent < 80) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Team Capacity Overview
        </CardTitle>
        <CardDescription>
          Today's workload distribution across teams
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {capacityData.map((team) => (
            <div key={team.teamId} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{team.teamName}</span>
                  {team.isDefault && (
                    <Badge variant="secondary" className="text-xs">Default</Badge>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {team.utilizationPercent}% utilized
                </span>
              </div>

              <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className={`h-full transition-all ${getUtilizationColor(team.utilizationPercent)}`}
                  style={{ width: `${team.utilizationPercent}%` }}
                />
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  <span>{team.technicianCount} techs</span>
                </div>
                <div className="flex items-center gap-1">
                  <Wrench className="h-3.5 w-3.5" />
                  <span>{team.activeTickets} tickets</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{formatMinutes(team.usedMinutes)} / {formatMinutes(team.totalCapacityMinutes)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
